import { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ArrowRight16, CloseCircle16, Column16, Plus16, Search16, Trash16 } from '@makyoapp/makyo-icon/icon'
import {
  Button,
  ButtonVariant,
  Field,
  JustifyVariant,
  Label,
  Modal,
  SelectDropdown,
  SimpleInput,
  Switch,
  Tabs,
} from '@makyoapp/makyo-ui'

import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useGridImportData } from '@/hooks/useGridImportData'
import { validateTableName } from '@/shared/ag-grid'
import { useDebounceInput } from '@/shared/makyo-hooks'
import { ColumnHeader, IMPORT_STEPS, ImportItem } from '@/utils/grid-import'
import { ColumnTypeIcon } from '@/components/ColumnTypeIcon'
import { useGridV2Context } from '@/components/GridV2/contexts/GridV2Context'
import { ScrollMenu, ScrollMenuItem } from '@/components/ScrollMenu'

const ColumnTypesLabelMap = {
  [ColumnTypes.Text]: 'Text',
  [ColumnTypes.URL]: 'URL',
  [ColumnTypes.Email]: 'Email',
  [ColumnTypes.Phone]: 'Phone',
  [ColumnTypes.RichText]: 'Rich Text',
  [ColumnTypes.RowID]: 'Row ID',
  [ColumnTypes.Emoji]: 'Emoji',
  [ColumnTypes.Icon]: 'Icon',
  [ColumnTypes.QRCode]: 'QR Code',
  [ColumnTypes.Barcode]: 'Barcode',
  [ColumnTypes.Slug]: 'Slug',
  [ColumnTypes.Color]: 'Color',
  [ColumnTypes.Address]: 'Address',
  [ColumnTypes.AISEO]: 'AI SEO',
  [ColumnTypes.AITextProcessing]: 'AI Text Processing',
  [ColumnTypes.Number]: 'Number',
  [ColumnTypes.Rating]: 'Rating',
  [ColumnTypes.Boolean]: 'Boolean',
  [ColumnTypes.DateTime]: 'Date Time',
  [ColumnTypes.MultiSelect]: 'Multi Select',
  [ColumnTypes.Array]: 'Array',
  [ColumnTypes.ImageArray]: 'Image Array',
  [ColumnTypes.Image]: 'Image',
  [ColumnTypes.Attachment]: 'Attachment',
  [ColumnTypes.Coordinate]: 'Coordinate',
  [ColumnTypes.Function]: 'Function',
  [ColumnTypes.Connection]: 'Connection',
  [ColumnTypes.Lookup]: 'Lookup',
  [ColumnTypes.Formula]: 'Formula',
  [ColumnTypes.Join]: 'Join',
  [ColumnTypes.Math]: 'Math',
  [ColumnTypes.Template]: 'Template',
  [ColumnTypes.SingleCell]: 'Single Cell',
  [ColumnTypes.SessionID]: 'Session ID',
}

/**
 * available column types for import
 */
const importColumnOptions = [
  ColumnTypes.Text,
  ColumnTypes.Number,
  ColumnTypes.Boolean,
  ColumnTypes.DateTime,
  ColumnTypes.URL,
  ColumnTypes.Email,
  ColumnTypes.Phone,
  ColumnTypes.RichText,
  ColumnTypes.Image,
  ColumnTypes.URL,
  ColumnTypes.Address,
  ColumnTypes.Color,
  ColumnTypes.Rating,
  ColumnTypes.Icon,
  ColumnTypes.Attachment,
]

/**
 * GridImportPreviewData Component
 */
const GridImportPreviewData = () => {
  const { t } = useTranslation()

  // access import state
  const { importState, setImportState, prevStep, nextStep } = useGridImportData()

  // derived state
  const currentItem = importState.items[importState.activeSheet]
  const hasDisabledSheet = importState.items.some((item) => item.disabled)
  const disabledSheetCount = importState.items.filter((item) => item.disabled).length

  const enabledSheets = importState.items.filter((item) => !item.disabled)
  const enabledSheetsCount = enabledSheets.length

  /**
   * Handle disable sheet
   */
  const handleDisableSheet = (index: number) => {
    // we should not let user disable all the sheets
    if (disabledSheetCount === importState.items.length - 1) return

    const updatedItems = importState.items
    let updatedActiveSheet = importState.activeSheet

    // disable the sheet items
    updatedItems[index].disabled = true

    // if the disabled sheet is the active sheet, switch to the first enabled sheet
    if (index === importState.activeSheet) {
      const firstEnabledSheetIndex = importState.items.findIndex((item) => !item.disabled)
      updatedActiveSheet = firstEnabledSheetIndex
    }

    // Update import state
    setImportState((prev) => ({
      ...prev,
      items: updatedItems,
      activeSheet: updatedActiveSheet,
    }))
  }

  /**
   * Handle enable sheet
   */
  const handleEnableSheet = () => {
    // find first disabled sheet
    const firstDisabledSheetIndex = importState.items.findIndex((item) => item.disabled)
    if (firstDisabledSheetIndex === -1) return

    // update item
    const updatedItems = importState.items
    updatedItems[firstDisabledSheetIndex].disabled = false

    setImportState((prev) => ({ ...prev, items: updatedItems }))
  }

  return (
    <>
      <Modal.Body className='p-0 max-h-[70vh] overflow-scroll'>
        <div className='container w-[768px] max-w-full p-2 pt-0'>
          {/* Tabs for multiple items (sheets for Excel, files for CSV) */}
          {importState.items.length > 1 && (
            <div className='mb-4 pb-4 border-b bdr'>
              <div className='w-full flex items-center gap-4 justify-between'>
                <div className='text-light'>
                  {importState.fileType === 'excel'
                    ? `Available Sheet${enabledSheetsCount > 1 ? `s (${enabledSheetsCount})` : ''}`
                    : `Available File${enabledSheetsCount > 1 ? `s (${enabledSheetsCount})` : ''}`}
                </div>

                <div
                  role='button'
                  className='flex items-center gap-2 text-primary-500 cursor-pointer'
                  onClick={handleEnableSheet}
                >
                  <Plus16 width={12} height={12} />
                  {importState.fileType === 'excel' ? 'Add Sheet' : 'Add File'} ({disabledSheetCount})
                </div>
              </div>

              <div className='mt-4 w-full overflow-hidden'>
                <ScrollMenu>
                  {enabledSheets.map((item) => {
                    const isActive = item.index === importState.activeSheet

                    return (
                      <ScrollMenuItem
                        key={item.index}
                        itemId={item.index.toString()}
                        role='button'
                        onClick={() => setImportState((prev) => ({ ...prev, activeSheet: item.index }))}
                        className={clsx(
                          'min-w-[100px] max-w-[150px] truncate rounded-xs mr-3 flex gap-2 items-center justify-between transition',
                          isActive
                            ? 'border text-primary-500 border-primary-500'
                            : 'text-light border border-zinc-200 dark:border-slate-600 dark:hover:border-slate-500'
                        )}
                      >
                        <div className='truncate flex-1 py-2 pl-2.5 pr-0'>{item.name}</div>

                        <div
                          role='button'
                          title={t(importState.fileType === 'excel' ? 'Remove Sheet' : 'Remove File')}
                          className='p-2 pl-1 shrink-0 hover:text-red-600 dark:hover:text-red-400'
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDisableSheet(item.index)
                          }}
                        >
                          <CloseCircle16 width={13} height={13} />
                        </div>
                      </ScrollMenuItem>
                    )
                  })}
                </ScrollMenu>
              </div>
            </div>
          )}

          {currentItem && importState.type === 'import' && (
            <ImportItemEditor index={importState.activeSheet} item={currentItem} />
          )}

          {currentItem && importState.type === 'append' && (
            <AppendItemEditor index={importState.activeSheet} item={currentItem} />
          )}

          {/* <hr className='my-6 bdr' />

          <div>
            <div className='flex justify-between'>
              <Button variant={ButtonVariant.Outlined} onClick={() => setPreviewOpened(!previewOpened)}>
                <Trans>{previewOpened ? 'Hide Preview' : 'Preview'}</Trans>
              </Button>
            </div>
            {previewOpened && (
              <div className='container pt-5'>
                <DataEditor
                  width='100%'
                  getCellContent={getCellContent}
                  columns={getVisibleColumnHeaders}
                  smoothScrollX={true}
                  smoothScrollY={true}
                  rowMarkers='both'
                  headerIcons={headerIcons}
                  rows={10}
                  theme={currentTheme}
                />
              </div>
            )}
          </div> */}
        </div>
      </Modal.Body>

      {/* Modal Footer with Navigation Buttons */}
      {importState.step !== IMPORT_STEPS.SELECT_FILE && (
        <Modal.Footer>
          <div className='flex justify-between w-full'>
            <Button onClick={prevStep} variant={ButtonVariant.Outlined}>
              <Trans>Back</Trans>
            </Button>
            {Object.keys(currentItem?.columnHeaders).length > 0 && (
              <Button onClick={nextStep} variant={ButtonVariant.Normal}>
                <Trans>Next</Trans>
              </Button>
            )}
          </div>
        </Modal.Footer>
      )}
    </>
  )
}

interface ActiveItemEditorProps {
  index: number
  item: ImportItem
}

const ImportItemEditor = ({ index, item }: ActiveItemEditorProps) => {
  const [debouncedSearch, , , search, setSearch] = useDebounceInput('')

  // access tables
  const { tables } = useDbClientContext()

  // access import state
  const { updateItem, updateTableName } = useGridImportData()

  // derived state
  const columnHeaders = item.columnHeaders || []
  const excludeColumnHeaders = item.excludeColumnHeaders || []
  const canAddColumn = excludeColumnHeaders.length > 0

  const filteredColumnHeaders = debouncedSearch
    ? columnHeaders.filter((header) => header.title.toLowerCase().includes(debouncedSearch.toLowerCase()))
    : columnHeaders

  /**
   * Handle change column type
   */
  const handleChangeColumnType = (column: ColumnHeader, type: ColumnTypes) => {
    const updatedColumnHeaders = item.columnHeaders.map((col) =>
      col.id === column.id ? { ...col, columnType: type } : col
    )

    updateItem(index, { ...item, columnHeaders: updatedColumnHeaders })
  }

  const handleRemoveColumn = (columnId: string) => {
    // extract column from column headers
    const column = item.columnHeaders.find((col) => col.id === columnId)

    if (!column) return

    // remove column from column headers
    const updatedColumnHeaders = item.columnHeaders.filter((col) => col.id !== columnId)

    // add to exclude column headers if not already there
    const updatedExcludeColumnHeaders = [...item.excludeColumnHeaders, column]

    updateItem(index, {
      ...item,
      columnHeaders: updatedColumnHeaders,
      excludeColumnHeaders: updatedExcludeColumnHeaders,
    })
  }

  const handleAddMissingColumns = () => {
    // pick first excluded column to column headers
    const updatedColumnHeaders = [...item.columnHeaders, item.excludeColumnHeaders[0]]

    // remove first excluded column from exclude column headers
    const updatedExcludeColumnHeaders = item.excludeColumnHeaders.slice(1)

    updateItem(index, {
      ...item,
      columnHeaders: updatedColumnHeaders,
      excludeColumnHeaders: updatedExcludeColumnHeaders,
    })
  }

  // Add a ref to track if the change is from user input
  const isUserInput = useRef(false)
  const [debouncedTblName, , , tblName, setTblName] = useDebounceInput('')
  const [error, setError] = useState<string | null>(null)

  // Update the input value when active sheet changes
  useEffect(() => {
    isUserInput.current = false // Mark that this change is from sheet switch
    const currentTableName = item.name || ''
    setTblName(currentTableName)
  }, [index, item.name, setTblName])

  // Handle debounced table name changes
  useEffect(() => {
    // Only process if it's from user input
    if (isUserInput.current && debouncedTblName && debouncedTblName !== item.name) {
      const validationError = validateTableName(debouncedTblName, tables)
      if (validationError) {
        setError(validationError)
      } else {
        updateTableName(index, debouncedTblName)
        setError(null)
      }
    }
  }, [debouncedTblName, index, item.name, tables, updateTableName])

  return (
    <div className=''>
      <Field>
        <Label className='w-[20%]!'>
          <Trans>Rename Table</Trans>
        </Label>
        <div className='pt-1 w-full pr-8'>
          <SimpleInput
            error={error}
            value={tblName}
            onChange={(e) => {
              isUserInput.current = true // Mark that this change is from user input
              setTblName(e.currentTarget.value)
            }}
          />
        </div>
      </Field>

      {/* Ignore First Row Toggle */}
      <Field isCenter>
        <Label className='w-[20%]!'>
          <Trans>Ignore first row</Trans>
        </Label>
        <Switch
          checked={Boolean(item.isIgnoreFirstRow)}
          onClick={() => updateItem(index, { ...item, isIgnoreFirstRow: !item.isIgnoreFirstRow })}
          justify={JustifyVariant.Start}
        />
      </Field>

      {/* Columns Config */}
      <Field>
        <Label className='w-[20%]!'>
          <Trans>Configure Fields</Trans>
        </Label>

        <div className='w-full space-y-4'>
          <div className='pt-1 text-light'>Column Headers ({filteredColumnHeaders.length})</div>

          <div className='flex items-center gap-6 max-w-[87.5%]'>
            <SimpleInput
              outlined
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='Search column name'
              before={<Search16 className='mk-search-box-icon' />}
            />
          </div>

          <div className='space-y-4'>
            {filteredColumnHeaders.map((column) => {
              return (
                <div key={column.id} className='flex items-center gap-4'>
                  <div className='w-[45%] flex items-center  justify-between rounded-sm p-2 bg-zinc-200 dark:bg-slate-900'>
                    <div className='flex gap-2 items-center'>
                      <ColumnTypeIcon type={column.columnType as ColumnTypes} />
                      <span>{column.title}</span>
                    </div>
                    <ArrowRight16 width={14} height={14} className='text-light' />
                  </div>

                  <div className='w-[45%]'>
                    <SelectDropdown
                      toggleClassName='w-full border bdr'
                      value={column.columnType}
                      onChange={(opt) => handleChangeColumnType(column, opt as ColumnTypes)}
                      renderOption={(opt) => (
                        <div className='flex items-center gap-2'>
                          <ColumnTypeIcon type={opt as ColumnTypes} />
                          <span>{ColumnTypesLabelMap[opt as ColumnTypes]}</span>
                        </div>
                      )}
                      options={importColumnOptions}
                      withSearch
                    />
                  </div>

                  <div className='w-[10%] flex items-center'>
                    <Button
                      noPadding
                      squared
                      variant={ButtonVariant.Ghost}
                      onClick={() => handleRemoveColumn(column.id)}
                    >
                      <Trash16 />
                    </Button>
                  </div>
                </div>
              )
            })}

            {canAddColumn && (
              <Button variant={ButtonVariant.Outlined} onClick={handleAddMissingColumns}>
                <Plus16 width={14} height={14} className='mr-1' />
                Add Column ({item.excludeColumnHeaders.length})
              </Button>
            )}
          </div>
        </div>
      </Field>
    </div>
  )
}

const AppendItemEditor = ({ index, item }: ActiveItemEditorProps) => {
  // Local state
  const [debouncedSearch, , , search, setSearch] = useDebounceInput('')

  // access import state
  const { updateItem } = useGridImportData()

  // derived state
  const columnHeaders = item.columnHeaders || []
  const excludeColumnHeaders = item.excludeColumnHeaders || []
  const canAddColumn = excludeColumnHeaders.length > 0

  const filteredColumnHeaders = debouncedSearch
    ? columnHeaders.filter((header) => header.title.toLowerCase().includes(debouncedSearch.toLowerCase()))
    : columnHeaders

  // access current table
  const { table } = useGridV2Context()
  const tableColumns = table?.columns || []

  const filteredTableColumns = tableColumns.filter((column) => {
    const notMatched = !columnHeaders.some((col) => col.id === column.id)
    const notSlug = column.type !== ColumnTypes.Slug
    const notDeferred = ![
      ColumnTypes.Lookup,
      ColumnTypes.Connection,
      ColumnTypes.Function,
      ColumnTypes.Math,
      ColumnTypes.Template,
      ColumnTypes.SingleCell,
    ].includes(column.type)
    return notMatched && notSlug && notDeferred
  })

  const handleMatchColumn = (colHeader: ColumnHeader, colId: string) => {
    const column = tableColumns.find((col) => col.id === colId)
    if (!column) return

    const updatedColumnHeader = columnHeaders.map((header) =>
      header.id === colHeader.id
        ? {
            ...header,
            id: column.id,
            columnType: column.type,
          }
        : header
    )

    updateItem(index, { ...item, columnHeaders: updatedColumnHeader })
  }

  const handleRemoveColumn = (columnId: string) => {
    // extract column from column headers
    const column = item.columnHeaders.find((col) => col.id === columnId)

    if (!column) return

    // remove column from column headers
    const updatedColumnHeaders = item.columnHeaders.filter((col) => col.id !== columnId)

    // add to exclude column headers if not already there
    const updatedExcludeColumnHeaders = [...item.excludeColumnHeaders, column]

    updateItem(index, {
      ...item,
      columnHeaders: updatedColumnHeaders,
      excludeColumnHeaders: updatedExcludeColumnHeaders,
    })
  }

  const handleAddMissingColumns = () => {
    // pick first excluded column to column headers
    const updatedColumnHeaders = [...item.columnHeaders, item.excludeColumnHeaders[0]]

    // remove first excluded column from exclude column headers
    const updatedExcludeColumnHeaders = item.excludeColumnHeaders.slice(1)

    updateItem(index, {
      ...item,
      columnHeaders: updatedColumnHeaders,
      excludeColumnHeaders: updatedExcludeColumnHeaders,
    })
  }

  return (
    <div className=''>
      {/* Ignore First Row Toggle */}
      <Field isCenter>
        <Label className='w-[20%]!'>
          <Trans>Ignore first row</Trans>
        </Label>
        <Switch
          checked={Boolean(item.isIgnoreFirstRow)}
          onClick={() => updateItem(index, { ...item, isIgnoreFirstRow: !item.isIgnoreFirstRow })}
          justify={JustifyVariant.Start}
        />
      </Field>

      <Field>
        <Label className='w-[20%]!'>
          <Trans>Match Columns</Trans>
        </Label>

        <div className='w-full space-y-4'>
          <div className='pt-1 text-light'>Table Columns ({filteredColumnHeaders.length})</div>

          <div className='flex items-center gap-6 max-w-[93%]'>
            <SimpleInput
              outlined
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='Search column name'
              before={<Search16 className='mk-search-box-icon' />}
            />
          </div>

          <div className='space-y-4'>
            {filteredColumnHeaders.map((colHeader) => {
              const matchedTableColumn = tableColumns.find((column) => column.id === colHeader.id)?.id
              return (
                <div key={colHeader.originalIndex} className='flex items-center gap-4'>
                  <div className='w-[45%] flex items-center  justify-between rounded-sm p-2 bg-zinc-200 dark:bg-slate-900'>
                    <div className='flex gap-2 items-center'>
                      <Column16 width={16} height={16} />
                      <span>{colHeader.title}</span>
                    </div>
                    <ArrowRight16 width={14} height={14} className='text-light' />
                  </div>

                  <div className='w-[45%]'>
                    <SelectDropdown
                      toggleClassName='w-full border bdr'
                      value={matchedTableColumn ?? ''}
                      searchFilter={(opt, keyword) => {
                        const col = [...tableColumns, { id: '', name: 'Select Column', type: '' }].find(
                          (col) => col.id === opt
                        )
                        if (!col) return false
                        return col.name.toLowerCase().includes(keyword.toLowerCase())
                      }}
                      onChange={(colId) => {
                        handleMatchColumn(colHeader as ColumnHeader, colId)
                      }}
                      renderOption={(colId) => {
                        const column = [...tableColumns, { id: '', name: 'Select Column', type: '' }].find(
                          (col) => col.id === colId
                        )
                        if (!column) return null

                        return (
                          <div className={clsx('flex items-center gap-2', !column.type && 'text-light')}>
                            {column.type ? (
                              <ColumnTypeIcon type={column.type as ColumnTypes} />
                            ) : (
                              <Column16 width={16} height={16} />
                            )}
                            <span>{column.name}</span>
                          </div>
                        )
                      }}
                      options={[{ id: '', name: 'Select Column', type: '' }, ...filteredTableColumns].map(
                        (col) => col.id
                      )}
                      withSearch
                    />
                  </div>

                  <div className='w-[10%] flex items-center'>
                    <Button
                      noPadding
                      squared
                      variant={ButtonVariant.Ghost}
                      onClick={() => handleRemoveColumn(colHeader.id)}
                    >
                      <Trash16 />
                    </Button>
                  </div>
                </div>
              )
            })}

            {canAddColumn && (
              <Button variant={ButtonVariant.Outlined} onClick={handleAddMissingColumns}>
                <Plus16 width={14} height={14} className='mr-1' />
                Add Column ({item.excludeColumnHeaders.length})
              </Button>
            )}
          </div>
        </div>
      </Field>
    </div>
  )
}

export default GridImportPreviewData
